import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { setProfileButtonFocus } from '@actions/profileActions'
import { SecondaryButton } from '@components/Buttons/SecondaryButton/SecondaryButton'
import DotSpinner from '@components/DotSpinner/DotSpinner'
import { ProfileTitle } from '@components/Profile/ProfileTitle/ProfileTitle'
import { routes } from '@constants/routes.const'
import { setError } from '@actions/errorCodeActions'
import { ErrorCodes, ErrorActions, Language, LocalStorageItem } from '@constants/index'
import { VelvetSDK } from 'sdk'
import './Logout.scss'
import useGoBackNavigation from '@hooks/useGoBackNavigation'

interface LogoutProps {
  showMenu: (status: boolean) => void
}

export const Logout = ({ showMenu }: LogoutProps) => {
  const logoutButtonRef = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const V = VelvetSDK.Vocabulary.vocabulary
  const apiEndpointBaseUrl = VelvetSDK.Configuration.configuration.apiEndpointBaseUrl

  useGoBackNavigation()

  const logout = useCallback(async () => {
    try {
      if (apiEndpointBaseUrl) {
        const response = await VelvetSDK.Authentication.service.logout()
        localStorage.removeItem(LocalStorageItem.ACTIVE_PROFILE)
        return response
      }
    } catch (error) {
      console.error('Error with logout:', error)
    }
  }, [apiEndpointBaseUrl])

  const fetchVocabulary = useCallback(
    async (language?: string | null) => {
      await VelvetSDK.Vocabulary.service.fetchVocabulary(language)

      if (VelvetSDK.Vocabulary.vocabulary?.error) {
        const error = VelvetSDK.Vocabulary.vocabulary.error
        const errorUpdate = {
          showErrorModal: true,
          errorCode: ErrorCodes.FETCH_VOCABULARY,
          HTTPcode: error.status,
          fetchAction: ErrorActions.VOCABULARY,
        }

        dispatch(setError(errorUpdate))
      }
    },
    [dispatch],
  )

  const handleLogout = useCallback(() => {
    setLoading(true)
    sessionStorage.clear()
    logout()
      .then(async () => {
        localStorage.removeItem(LocalStorageItem.USER_DATA)
        await fetchVocabulary(Language.EN)
        setLoading(false)
        navigate(routes.LOGIN)
      })
      .catch(() => {
        setLoading(false)
      })
  }, [fetchVocabulary, logout, navigate])

  const navigateToSettings = useCallback(() => {
    navigate(routes.SETTINGS)
  }, [navigate])

  useEffect(() => {
    showMenu(false)
    logoutButtonRef.current?.focus()
    dispatch(setProfileButtonFocus(V.profilePreferencesLogOut))
  }, [showMenu, dispatch, V.profilePreferencesLogOut])

  return (
    <div className="logout-page">
      {loading ? (
        <DotSpinner />
      ) : (
        <div className="logout-container">
          <ProfileTitle title={V.logOutProfileTitle} />
          <SecondaryButton onClick={handleLogout}>{V.logoutButtonLabel}</SecondaryButton>
          <SecondaryButton onClick={navigateToSettings} buttonRef={logoutButtonRef}>
            {V.cancelButtonLabel}
          </SecondaryButton>
        </div>
      )}
    </div>
  )
}
